import * as React from 'react';
import executeContextual from '../utils/entryPointOperations/executeContextual';
import MinorFrame from './Compliance/MinorFrame';
import strings from 'owa-strings/owa-extensibility-strings';
import { ActionButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { AddinCommandsManifestCacheProvider } from 'owa-addins-osf-facade';
import { AddinIcons } from 'owa-addins-icons';
import { Callout } from 'office-ui-fabric-react/lib/Callout';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { IOverflowSet, OverflowSet } from 'office-ui-fabric-react/lib/OverflowSet';
import { isMinorForbidden } from '../utils/entryPointOperations/AddinChecker';
import { observer } from 'mobx-react';
import { OsfHostContainer } from '../components/OsfHostContainer';
import 'owa-addins-osfruntime';
import {
    ContextualAddinCommand,
    getExtensibilityState,
    terminateContextualCallout,
} from 'owa-addins-store';

const style = require('./ContextualCallout.scss');

const CONTEXTUAL_CALLOUT_WIDTH = '570px';
const CONTEXTUAL_CALLOUT_HEIGHT_DEFAULT = '350px';

@observer
export default class ContextualCallout extends React.Component<{}, {}> {
    private commandBar: IOverflowSet;

    constructor(props: any, context?: any) {
        super(props, context);
        this.refCallback = this.refCallback.bind(this);
        this.focusOnAddinBar = this.focusOnAddinBar.bind(this);
        this.dismissCallout = this.dismissCallout.bind(this);
        this.getContextualExecutor = this.getContextualExecutor.bind(this);
        this.convertAddinsToCommandBarItems = this.convertAddinsToCommandBarItems.bind(this);
    }

    public dismissCallout(): void {
        if (!!getExtensibilityState().activeDialog) {
            return;
        }

        getExtensibilityState().contextualCalloutState.contextualAnchor.focus();
        terminateContextualCallout();
    }

    private focusOnAddinBar(): void {
        this.commandBar.focus();
    }

    private getContextualExecutor(contextual: ContextualAddinCommand): () => void {
        return () => {
            const {
                hostItemIndex,
                addinCommand,
            } = getExtensibilityState().runningContextualAddinCommand;
            if (contextual.get_Id() !== addinCommand.get_Id()) {
                executeContextual(hostItemIndex, contextual);
            }
        };
    }

    public convertAddinsToCommandBarItems(
        activeAddins: ContextualAddinCommand[],
        selectedAddin: ContextualAddinCommand
    ): IContextualMenuItem[] {
        return activeAddins.map(addin => {
            const isSelected: boolean = addin.get_Id() === selectedAddin.get_Id();
            return {
                key: addin.get_Id(),
                className: isSelected && style.selected,
                text: addin.detectedEntity.Label,
                onClick: this.getContextualExecutor(addin),
            };
        });
    }

    private refCallback(commandBar: IOverflowSet) {
        this.commandBar = commandBar;
    }

    private onRenderContextualAddin(addin: IContextualMenuItem): JSX.Element {
        return (
            <ActionButton
                className={addin.className}
                onClick={addin.onClick as () => void}
                text={addin.text}
                ariaLabel={addin.text}
            />
        );
    }

    private onRenderOverflowButton(overflowItems: IContextualMenuItem[]): JSX.Element {
        return (
            <IconButton
                menuIconProps={{ iconName: 'More' }}
                menuProps={{ items: overflowItems! }}
                ariaLabel={strings.overflowMenuAccessibilityLabel}
            />
        );
    }

    render() {
        const state = getExtensibilityState();
        if (!state.runningContextualAddinCommand) {
            return <div />;
        }

        const anchor = state.contextualCalloutState.contextualAnchor;
        const { hostItemIndex, addinCommand } = state.runningContextualAddinCommand;
        const contextualAddin: ContextualAddinCommand = addinCommand as ContextualAddinCommand;
        const addinBarItems = this.convertAddinsToCommandBarItems(
            state.contextualCalloutState.activeContextualAddinCommands,
            contextualAddin
        );
        const iframeStyle = {
            height: !contextualAddin.detectedEntity.RequestedHeight
                ? CONTEXTUAL_CALLOUT_HEIGHT_DEFAULT
                : contextualAddin.detectedEntity.RequestedHeight + 'px',
        };

        const hostActions = {};
        hostActions[OSF.AgaveHostAction.TabExit] = this.focusOnAddinBar;
        hostActions[OSF.AgaveHostAction.TabExitShift] = this.focusOnAddinBar;
        hostActions[OSF.AgaveHostAction.EscExit] = this.dismissCallout;

        const addinPart = isMinorForbidden(contextualAddin) ? (
            <MinorFrame onClickUninstall={this.dismissCallout} />
        ) : (
            <OsfHostContainer
                addinCommand={state.runningContextualAddinCommand.addinCommand}
                controlId={state.runningContextualAddinCommand.controlId}
                hostItemIndex={hostItemIndex}
                manifestCacheProvider={AddinCommandsManifestCacheProvider}
                style={iframeStyle}
                notifyHostActions={hostActions}
            />
        );

        return (
            <Callout
                gapSpace={0}
                target={anchor}
                onDismiss={this.dismissCallout}
                setInitialFocus={true}
                calloutWidth={parseInt(CONTEXTUAL_CALLOUT_WIDTH)}
                beakWidth={19}
                className={style.divContainer}>
                <Icon className={style.appIcon} iconName={AddinIcons.OfficeAddinsLogo} />
                <div className={style.divider} />
                <OverflowSet
                    componentRef={this.refCallback}
                    items={addinBarItems}
                    className={style.contextualBar}
                    onRenderItem={this.onRenderContextualAddin}
                    onRenderOverflowButton={this.onRenderOverflowButton}
                />
                <div className={style.contextualAddinFrame}>{addinPart}</div>
            </Callout>
        );
    }
}
